import React, { useEffect, useRef, useState } from "react";
import { jsPDF } from "jspdf";
import { shuffle } from "lodash";
import { useSelector, useDispatch } from "react-redux";
import { useGetSippsWorksheetDataMutation } from "../../slices/authApi";
import { 
  setTextImages, 
  selectTextImages,
  selectSelectedWorksheet,
  selectSippsLesson,
  selectSippsLevel,
  selectIncludesReview,
  selectIncludeComprehension,
  selectIncludeImages,
  selectFormData
 } from "../../slices/form";
import classes from "./QuestionsReview.module.css"; 
import newStyles from "./MatchingForm.module.css"; 
import ActionButton from "../UI/ActionButton/ActionButton";
// Import useNavigate from react-router-dom
import { useNavigate } from "react-router-dom";
import { PageLoadingSpinner } from "../UI/spinner/spinner";
import ProgressBarWithCircles from "../Layout/ProgressBar";
import {MATCHING_TYPES} from '../../constants';
import {useAppUsageLogger} from "../../slices/authApi";


function MatchingForm() {
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(true);
  const [downloaded, setDownloaded] = useState(false);
  const [getSippsWorksheetDataMutation, { data, error }] =
    useGetSippsWorksheetDataMutation();
  const retrievalStarted = useRef(false);

  const selectedWorksheet = useSelector(selectSelectedWorksheet);
  const sippsLevel = useSelector(selectSippsLevel);
  const sippsLesson = useSelector(selectSippsLesson);
  const includesReview = useSelector(selectIncludesReview);
  const includeComprehension = useSelector(selectIncludeComprehension);
  const includeImages = useSelector(selectIncludeImages);
  const formData = useSelector(selectFormData);

  const textImages = useSelector(selectTextImages);

  const [spanishWords, setSpanishWords] = useState([]);
  const [englishWords, setEnglishWords] = useState([]);
  const [unshuffledPairs, setUnshuffledPairs] = useState([]);
  const [images, setImages] = useState([]);
  const [readingText, setReadingText] = useState('');
  const [readingTitle, setReadingTitle] = useState('');
  const [readingQuestions, setReadingQuestions] = useState('');
  const [wordBank, setWordBank] = useState([]);
  const [fibQuestions, setfibQuestions] = useState([]);
  const logAppUse = useAppUsageLogger();
 
  const title = MATCHING_TYPES[selectedWorksheet] || "Worksheet";
  const formattedTitle = title.toLowerCase().split(' ').join('_');
  const fileName = `${formattedTitle}.pdf`;

  // Use navigate hook for navigation
  const navigate = useNavigate();

  useEffect(() => {
    if (selectedWorksheet !== null && !retrievalStarted.current) {
      retrievalStarted.current = true;
      getSippsWorksheetDataMutation({
        worksheet: selectedWorksheet,
        sipps_level: sippsLevel,
        sipps_lesson: sippsLesson,
        includes_review: includesReview,
        include_comprehension: includeComprehension,
        include_images: includeImages,
        image_speed: formData?.image_speed,
      });
    }
  }, [selectedWorksheet, sippsLevel, sippsLesson, includesReview, includeComprehension, includeImages, formData?.image_speed, getSippsWorksheetDataMutation]);


  useEffect(() => {
    if (data) {
      setLoading(false);
      if (selectedWorksheet === 7) {
        setReadingTitle(data.reading_title);
        setReadingText(data.reading_text);
        setReadingQuestions(data.reading_questions);
        dispatch(setTextImages(data.images));

      }else if (selectedWorksheet === 8) {
        const extractedQuestions = data.questions.map(q => q.question);
        const extractedAnswers = data.questions.map(q => q.answer);
        setfibQuestions(extractedQuestions);
        setWordBank(shuffle(extractedAnswers));
      }
      else {
        setSpanishWords(data.pairs.map((pair) => pair.spanish));
        setEnglishWords(data.pairs.map((pair) => pair.english));
        setUnshuffledPairs(data.unshuffled_pairs);

        if (
          selectedWorksheet === 2 ||
          selectedWorksheet === 3 ||
          selectedWorksheet === 4 ||
          selectedWorksheet === 6
        ) {
          setImages(data.pairs.map((pair) => pair.image));
        }
      }
    }
  }, [data, selectedWorksheet, dispatch]);

    
  const downloadPDF = () => {
    logAppUse("W"+selectedWorksheet+"_"+sippsLevel+"_"+sippsLesson, "sipps_pdf");
    const doc = new jsPDF();
    const pageWidth = doc.internal.pageSize.width;
    const pageHeight = doc.internal.pageSize.height;
    // Centered-text helper compatible with jsPDF v2 and v1.x (no align/options usage)
    const textCentered = (text, _x, y) => {
      const content = Array.isArray(text) ? text.join(" ") : String(text ?? "");
      const textWidth = doc.getTextWidth(content || "");
      const startX = (pageWidth - textWidth) / 2;
      doc.text(content, startX, y);
    };
    const initialYPosition = 40;
    const rowHeight = 40;
    const imageWidth = 30;
    const imageHeight = 30;
    let currentY = initialYPosition;
  
    // Define column positions

    const column1X = pageWidth / 5 - 20; // Spanish for 1, 5
    const column2X = (2 * pageWidth) / 3 + 10; // English for 1, 5

    // Define column positions for worksheets 3, 4 (English and Image)
    const englishColumnX = pageWidth / 3 -20;
    const imageColumnX = (2 * pageWidth) / 3;

    // Define column positions for worksheets 2, 6, else (Spanish, English, Image)
    const spanishColumnX = 10;
    const english3ColX = pageWidth / 3 + 30;
    const image3ColX = (2 * pageWidth) / 3 + 25;
  
    // Define maximum text width for wrapping
    const maxTextWidth = (pageWidth / 3)+10;
  
    // Set title for the PDF
    doc.setFont("NotoSans-Regular", "normal");

    doc.setFontSize(32);
    // doc.text(title, 105, 15, null, null, "center");
    textCentered(title, pageWidth / 2, 15);

    const addFooter = (doc) => {
      const currentFontSize = doc.internal.getFontSize();  // Store current font size
      // Set the footer font size
      doc.setFontSize(10);
      // doc.text(`© 2024-2025 ClassGenius. All rights reserved.`, doc.internal.pageSize.width / 2, doc.internal.pageSize.height - 10, { align: 'center' });
      textCentered(`© 2024-2025 ClassGenius. All rights reserved.`, doc.internal.pageSize.width / 2, doc.internal.pageSize.height - 10);
      // Restore the previous font size
      doc.setFontSize(currentFontSize);
    };
    const adjustFontSizeForWord = (word, maxWidth, doc) => {
      let fontSize = doc.internal.getFontSize();  // Get the current font size
      let textWidth = doc.getTextWidth(word);
  
      // If the text width exceeds the max width, reduce the font size
      while (textWidth > maxWidth && fontSize > 8) {  // Set a minimum font size of 8 to avoid unreadable text
          fontSize -= 2;  // Decrease font size
          doc.setFontSize(fontSize);
          textWidth = doc.getTextWidth(word);
      }
  
      return fontSize;
  };
  
  
    // Function to split long words and adjust y position
    const splitLongWordsAndAdjustPosition = (word, maxWidth, doc, x, y) => {
      let originalFontSize = doc.internal.getFontSize();  // Store original font size

      // Adjust font size for the word if it's too long
      adjustFontSizeForWord(word, maxWidth, doc);
      if (doc.getTextWidth(word) > maxWidth) {
        const middle = Math.floor(word.length / 2);
        const firstPart = word.slice(0, middle) + '-';
        const secondPart = word.slice(middle);
        
        doc.text(firstPart, x, y); // Print the first part
        y += rowHeight / 2; // Move to the next line for the second part
        doc.text(secondPart, x, y); // Print the second part
      } else {
        doc.text(word, x, y); // If the word fits, print it as is
      }
      doc.setFontSize(originalFontSize);
      return y; // Return the updated y position
    };
    const splitTextBySentences = (text) => {
      return text.match(/[^.!?]+[.!?]+[\])'"`’”]*/g) || [text];
    };
  
    if (selectedWorksheet === 7) {
      const wordCount = readingText.split(/\s+/).length;
      let fontSize = wordCount < 50 ? 26 : wordCount < 100 ? 22 : wordCount < 200 ? 19 : 16;
      doc.setFontSize(fontSize);
      // Set bold font for the title
      doc.setFont("NotoSans-Regular", "bold");
      // Center the title by calculating the middle of the page
      // doc.text(readingTitle, pageWidth / 2, currentY, { align: "center" });
      textCentered(readingTitle, pageWidth / 2, currentY);
      // Reset font to normal for the rest of the text
      doc.setFont("NotoSans-Regular", "normal");
      currentY += rowHeight / 2;

      // Image handling for worksheet 7
      let textStartX = 20;
      let imageBottom = currentY;
      let imageWidth = 0;

      if (textImages && textImages.length > 0) {
        // Calculate image dimensions while maintaining aspect ratio
        const maxImageWidth = pageWidth / 3 + 25;
        const maxImageHeight = pageHeight / 3 + 25;
        
        const img = new Image();
        img.src = `${textImages[0]}`;
        
        let scaledWidth = maxImageWidth;
        let scaledHeight = (img.height * maxImageWidth) / img.width;
        
        if (scaledHeight > maxImageHeight) {
          scaledHeight = maxImageHeight;
          scaledWidth = (img.width * maxImageHeight) / img.height;
        }
        
        // Larger paragraph text yields higher start, have image match
        let imageTop = currentY - 5;
        if (sippsLevel === "beginning" && wordCount < 100)
          imageTop = currentY - 7;
        else
          imageTop = currentY - 5;

        // Instead of the static 'JPEG' type, use the image type from the image itself
        const imageType = textImages[0].match(/^data:image\/(.*?);/i)?.[1]?.toUpperCase();
        doc.addImage(
          `${textImages[0]}`,
          imageType,
          20,
          imageTop,
          scaledWidth,
          scaledHeight
        );

        imageWidth = scaledWidth;
        imageBottom = currentY + scaledHeight + 10;
      }

      // In the text handling section for both beginning and non-beginning cases:
      if (sippsLevel === "beginning" && wordCount < 100) {
        const sentences = splitTextBySentences(readingText);
        let currentSentence = 0;
      
        while (currentSentence < sentences.length) {
          if (currentY > pageHeight - rowHeight) {
            addFooter(doc);
            doc.addPage();
            currentY = initialYPosition;
          }
      
          // Check if we should still be processing text beside the image
          const linesUntilImageBottom = Math.floor((imageBottom - currentY) / (rowHeight / 4));
          if (currentY <= imageBottom && linesUntilImageBottom > 0) {
            // Process text beside image
            const currentTextWidth = pageWidth - 40 - imageWidth - 10;
            textStartX = 20 + imageWidth + 10;
            
            // Split current sentence to fit beside image
            const sentenceLines = doc.splitTextToSize(sentences[currentSentence].trim(), currentTextWidth);
            const linesToProcess = Math.min(linesUntilImageBottom, sentenceLines.length);
            
            if (linesToProcess < sentenceLines.length) {
              // If sentence won't fit entirely beside image, process it below image instead
              currentY = Math.max(currentY, imageBottom);
              const fullWidthLines = doc.splitTextToSize(sentences[currentSentence].trim(), pageWidth - 40);
              // eslint-disable-next-line no-loop-func
              fullWidthLines.forEach(line => {
                if (currentY > pageHeight - rowHeight/2) {
                  addFooter(doc);
                  doc.addPage();
                  currentY = initialYPosition;
                }
                doc.text(line, 20, currentY);
                currentY += rowHeight / 4;
              });
            } else {
              // Process sentence beside image
              // eslint-disable-next-line no-loop-func
              sentenceLines.forEach(line => {
                doc.text(line, textStartX, currentY);
                currentY += rowHeight / 4;
              });
            }
            // Add extra space between sentences
            currentY += rowHeight / 10;
            currentSentence++;
          } else {
            // Process all remaining sentences at full width
            currentY = Math.max(currentY, imageBottom);
            for (let i = currentSentence; i < sentences.length; i++) {
              const fullWidthLines = doc.splitTextToSize(sentences[i].trim(), pageWidth - 40);
              // eslint-disable-next-line no-loop-func
              fullWidthLines.forEach(line => {
                if (currentY > pageHeight - rowHeight/2) {
                  addFooter(doc);
                  doc.addPage();
                  currentY = initialYPosition;
                }
                doc.text(line, 20, currentY);
                currentY += rowHeight / 4;
              });
              // Add extra space between sentences
              currentY += rowHeight / 10;
            }
            break;
          }
        }
      } else {
        // Non-beginning level text - same logic applies
        let remainingText = readingText;
        let textPosition = 0;

        while (remainingText.length > 0) {
          if (currentY > pageHeight - rowHeight/2 ) {
            addFooter(doc);
            doc.addPage();
            currentY = initialYPosition;
          }

          const linesUntilImageBottom = Math.floor((imageBottom - currentY) / (rowHeight / 4));
          if (currentY <= imageBottom && linesUntilImageBottom > 0) {
            // Process text beside image
            const currentTextWidth = pageWidth - 40 - imageWidth - 10;
            textStartX = 20 + imageWidth + 10;
            
            const nextLines = doc.splitTextToSize(remainingText, currentTextWidth);
            const linesToProcess = Math.min(linesUntilImageBottom, nextLines.length);
            
            for (let i = 0; i < linesToProcess; i++) {
              doc.text(nextLines[i], textStartX, currentY);
              currentY += rowHeight / 4;
              textPosition += nextLines[i].length + 1;
            }
            remainingText = remainingText.slice(textPosition).trim();
          } else {
            // Process all remaining text at full width
            currentY = Math.max(currentY, imageBottom); // Ensure we start below image
            const fullWidthLines = doc.splitTextToSize(remainingText, pageWidth - 40);
            // eslint-disable-next-line no-loop-func
            fullWidthLines.forEach(line => {
              if (currentY > pageHeight - rowHeight/2) {
                addFooter(doc);
                doc.addPage();
                currentY = initialYPosition;
              }
              doc.text(line, 20, currentY);
              currentY += rowHeight / 4;
            });
            break;
          }
        }
      }
      // Add a new page for the questions
      addFooter(doc);
      if (includeComprehension){
        doc.addPage();
        currentY = initialYPosition;
        doc.setFontSize(fontSize);
        doc.text("Questions:", 20, currentY);
        currentY += rowHeight / 2;
    
        // Add questions with text wrapping
        readingQuestions.forEach((question, index) => {
          if (currentY > pageHeight - rowHeight/2) {
            addFooter(doc);
            doc.addPage();
            currentY = initialYPosition;
          }
          const questionLines = doc.splitTextToSize(`${index + 1}. ${question}`, pageWidth - 40);
          questionLines.forEach((line) => {
            if (currentY > pageHeight - rowHeight/2) {
              addFooter(doc);
              doc.addPage();
              currentY = initialYPosition;
            }
            doc.text(line, 20, currentY);
            currentY += rowHeight / 4;
          });
          currentY += rowHeight / 4;
      });
    }
    addFooter(doc);
  
    } else if (selectedWorksheet === 8) {
      // Word bank and fill-in-the-blank worksheet for worksheet 8
      doc.setFontSize(16);
      doc.text("Wordbank:", 20, currentY);
      currentY += rowHeight / 4;
      let wordBankFirstHalf = wordBank.slice(0, Math.ceil(wordBank.length / 2));
      let wordBankSecondHalf = wordBank.slice(Math.ceil(wordBank.length / 2));
      doc.text(wordBankFirstHalf.join("    "), 20, currentY);
      currentY += rowHeight / 4;
      doc.text(wordBankSecondHalf.join("    "), 20, currentY);
      currentY += rowHeight/2;
  
      //doc.setFontSize(24);
      doc.text("Questions:", 20, currentY);
      currentY += rowHeight/4;
  
      fibQuestions.forEach((question, index) => {
        if (currentY > pageHeight - rowHeight) {
          addFooter(doc);
          doc.addPage();
          currentY = initialYPosition;
        }
        const questionLines = doc.splitTextToSize(`${index + 1}. ${question}`, pageWidth - 40);
        questionLines.forEach((line) => {
          if (currentY > pageHeight - rowHeight) {
            doc.addPage();
            currentY = initialYPosition;
          }
          doc.text(line, 20, currentY);
          currentY += rowHeight / 4;
        });
        currentY += rowHeight / 4;
      });
      addFooter(doc);
  
    } else if ([1, 5].includes(selectedWorksheet)) {
      // Worksheets 1 and 5: Only English and Spanish
      doc.setFontSize(32);
      const rowCount = Math.max(spanishWords.length, englishWords.length);

      for (let index = 0; index < rowCount; index++) {
          if (currentY > pageHeight - rowHeight) {
              addFooter(doc);
              doc.addPage();
              currentY = initialYPosition;
          }

          // Add Spanish word in column 1
          if (spanishWords[index]) {
              doc.text(spanishWords[index], column1X, currentY);
          }

          // Add English word in column 2
          if (englishWords[index]) {
              doc.text(englishWords[index], column2X, currentY);
          }

          currentY += rowHeight;
      }
      addFooter(doc);

      // Add an answer key for the shuffled foreign words as teacher may not know the language
      doc.addPage();
      // doc.text("Answer Key", 105, 15, null, null, "center");
      textCentered("Answer Key", pageWidth / 2, 15);
      doc.setFontSize(16);
      const keyRowHeight = 12;
      currentY = initialYPosition;
      for (let index = 0; index < rowCount; index++) {
        if (currentY > pageHeight - keyRowHeight) {
            addFooter(doc);
            doc.addPage();
            currentY = initialYPosition;
        }
        
        if (unshuffledPairs[index]) {
          // Add Spanish/Tongan word and adjust position if it's split
          currentY = splitLongWordsAndAdjustPosition(unshuffledPairs[index][0], maxTextWidth, doc, pageWidth / 3 - 20, currentY);
          // Add English word and adjust position if it's split
          currentY = splitLongWordsAndAdjustPosition(unshuffledPairs[index][1], maxTextWidth, doc, (2 * pageWidth) / 3, currentY);      
        }

        currentY += keyRowHeight;
      }
      addFooter(doc);
    
  } else if (selectedWorksheet === 3) {
    // Worksheet 3: Only English and Image, no special boxes
    doc.setFontSize(32);
    const rowCount = Math.max(englishWords.length, images.length);

    for (let index = 0; index < rowCount; index++) {
        if (currentY > pageHeight - rowHeight) {
            addFooter(doc);
            doc.addPage();
            currentY = initialYPosition;
        }

        // Add English word in column 1
        if (englishWords[index]) {
            doc.text(englishWords[index], englishColumnX, currentY);
        }

        // Add image in column 2
        if (images[index]) {
            doc.addImage(images[index], "PNG", imageColumnX, currentY-10, imageWidth, imageHeight);
        }

        currentY += rowHeight;
    }
    addFooter(doc);

} else if (selectedWorksheet === 4) {
    // Worksheet 4: English with dotted boxes and Image with solid boxes
    doc.setFontSize(32);
    const rowCount = Math.max(englishWords.length, images.length);

    for (let index = 0; index < rowCount; index++) {
        if (currentY > pageHeight - rowHeight) {
            addFooter(doc);
            doc.addPage();
            currentY = initialYPosition;
        }

        // Add English word with a dotted box around it
        if (englishWords[index]) {
            const textWidth = doc.getTextWidth(englishWords[index]);
            doc.setLineDash([3, 3], 0);  // Set dashed line style for the box
            doc.rect(englishColumnX - 2, currentY - 10, textWidth + 4, rowHeight / 2 + 2);  // Adjust box size and position
            doc.text(englishWords[index], englishColumnX, currentY);
        }

        // Add image with a solid box around it
        if (images[index]) {
            doc.addImage(images[index], "PNG", imageColumnX, currentY-10, imageWidth, imageHeight);
            doc.setLineDash([3,3],0);  // Solid line for image box
            doc.rect(imageColumnX - 2, currentY - 12, imageWidth + 4, imageHeight + 4);  // Box slightly bigger than the image
        }

        currentY += rowHeight;
    }
    addFooter(doc);
} else if (selectedWorksheet === 2 || selectedWorksheet === 6) {
  // Worksheets 2 and 6: Use split function logic for Spanish, English, and Images
  doc.setFontSize(32);
  const rowCount = Math.max(spanishWords.length, englishWords.length, images.length);

  for (let index = 0; index < rowCount; index++) {
      if (currentY > pageHeight - rowHeight) {
          addFooter(doc);
          doc.addPage();
          currentY = initialYPosition;
      }

      // Add Spanish word and adjust position if it's split
      if (spanishWords[index]) {
          currentY = splitLongWordsAndAdjustPosition(spanishWords[index], maxTextWidth, doc, spanishColumnX, currentY);
      }

      // Add English word and adjust position if it's split
      if (englishWords[index]) {
          currentY = splitLongWordsAndAdjustPosition(englishWords[index], maxTextWidth, doc, english3ColX, currentY);
      }

      // Add image if available
      if (images[index]) {
          doc.addImage(images[index], "PNG", image3ColX, currentY-10, imageWidth, imageHeight);
      }

      currentY += rowHeight;
  }
  addFooter(doc);
} else {
      // Handle other worksheets (same logic as before in the 'else' block, use split for long words)
      doc.setFontSize(32);
      const rowCount = Math.max(spanishWords.length, englishWords.length, images.length);

      for (let index = 0; index < rowCount; index++) {
          if (currentY > pageHeight - rowHeight) {
              addFooter(doc);
              doc.addPage();
              currentY = initialYPosition;
          }

          // Add Spanish word and adjust position if it's split
          if (spanishWords[index]) {
              currentY = splitLongWordsAndAdjustPosition(spanishWords[index], maxTextWidth, doc, spanishColumnX, currentY);
          }

          // Add English word and adjust position if it's split
          if (englishWords[index]) {
              currentY = splitLongWordsAndAdjustPosition(englishWords[index], maxTextWidth, doc, english3ColX, currentY);
          }

          // Add image if available
          if (images[index]) {
              doc.addImage(images[index], "PNG", image3ColX, currentY, imageWidth, imageHeight);
          }

          currentY += rowHeight;
      }
      addFooter(doc);
    }

      
    doc.save(fileName);
    setDownloaded(true);
  };

  
  
  function QuestionList(questions) {
    return (
      <div style={{ whiteSpace: 'pre-line' }}>
      {questions && questions.length > 0 ? questions.map((str, index) => `${index + 1}. ${str}`).join('\n') : null}
      </div>
    );
  }
  

  const backHandler = () => {
    navigate(-1);
  };

  const getMatchingWorksheetReview = () => {
    return (
      spanishWords.map((word, index) => (
        <div key={index} className={newStyles.alignItem}>
          <div className={newStyles.paragraphBox}>
            {word && <strong>{word}</strong>}
          </div>
          {selectedWorksheet === 3 || selectedWorksheet === 4 ? (
            <>
              <div
                className={`${newStyles.paragraphBox} ${newStyles.englishColumn}`}
              >
                {englishWords[index]}
              </div>
              <div
                className={`${newStyles.paragraphBox} ${newStyles.imageColumn}`}
              >
                {images[index] && (
                  <img
                    src={`data:image/png;base64,${images[index]}`}
                    alt={`img-${index}`}
                    className={newStyles.matchingImg}
                  />
                )}
              </div>
            </>
          ) : (
            <>
              <div className={newStyles.paragraphBox}>
                {englishWords[index]}
              </div>
              {(selectedWorksheet === 2 ||
                selectedWorksheet === 6) &&
                images[index] && (
                  <div className={newStyles.paragraphBox}>
                    <img
                      src={`data:image/png;base64,${images[index]}`}
                      alt={`img-${index}`}
                      className={newStyles.matchingImg}
                    />
                  </div>
                )}
            </>
          )}
        </div>
      ))
    );
  };
  const getFillInTheBlankReview = () => {
  if (!data || !data.questions) return null;

  const spacedWords = wordBank.join("    ");

  return (
    <div className={newStyles.fibBox}> 
      <h3>Wordbank:</h3>
      <div className={newStyles.wordBankItem}>
        {spacedWords}
      </div>
      <h3>Questions:</h3>
      {fibQuestions.map((question, index) => (
        <div key={index} > 
          <p>{index + 1}. {question}</p>
        </div>
      ))}
    </div>
  );
};


  

  if (error) return <div>Error: {error.message}</div>;

  return (
    <>
    <div className={classes.pageMargins}>
      {loading ? <PageLoadingSpinner />:<></>}

      <p style={{ fontSize: "larger", textAlign: "center" }}>
        <strong style={{ fontSize: "19px" }}>Review worksheet content.</strong>
        <br />
        Download a PDF below, or go home to start again.
      </p>
      <div style={{ paddingTop: "60px" }} />

      <ProgressBarWithCircles progress={downloaded ? 100 : 75} />

      <div className={newStyles.innerContainer}>
        <h2 className={classes.title} style={{ textAlign: "center" }}>
          {selectedWorksheet === 7 ? readingTitle : title}
        </h2>
        {selectedWorksheet === 7 ? (
            <div className={newStyles.readingBox}>
              {textImages && textImages.length > 0 && (
                <img
                  src={`${textImages[0]}`}
                  alt="Reading illustration"
                  className={newStyles.passageImage}
                />
              )}
              {readingText}
              <br />
              <br />
              {QuestionList(readingQuestions)}
            </div>
          ) : selectedWorksheet === 8 ? (
          getFillInTheBlankReview()
        ) : (
          getMatchingWorksheetReview()
        )}
      </div>
  <div className={`${classes['form-actions']} ${classes['form-actions--terminal']}`}>
        <ActionButton
          text="Download PDF"
          subText="" // or provide subText if needed
          variant="secondary"
          handleOnClick={downloadPDF}
        />
        <ActionButton
          text="Back"
          variant="secondary"
          handleOnClick={backHandler}
        />
      </div>
    </div>
    </>
  );
}

export default MatchingForm;
